import { Button } from "@/components/ui/button"
import { Loading } from "@/components/ui/loading"
import { useCompleteCartOrder } from "@/lib/hooks/use-checkout"
import { setStoredCart } from "@/lib/utils/cart"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"

export const Route = createFileRoute("/$countryCode/order/confirmed")({
  validateSearch: (search) => ({
    cart_id: typeof search.cart_id === "string" ? search.cart_id : undefined,
    redirect_status:
      typeof search.redirect_status === "string"
        ? search.redirect_status
        : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Completing Order | +Bake Cafe" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: StripeReturnPage,
})

function StripeReturnPage() {
  const { countryCode } = Route.useParams()
  const { cart_id, redirect_status } = Route.useSearch()
  const navigate = useNavigate()
  const completeCartOrder = useCompleteCartOrder()
  const didCompleteRef = useRef(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (didCompleteRef.current) {
      return
    }

    if (!cart_id) {
      setError("We could not find the checkout cart for this payment.")
      return
    }

    setStoredCart(cart_id)

    if (redirect_status === "failed") {
      setError(
        "Stripe could not confirm the payment. Please try checkout again.",
      )
      return
    }

    didCompleteRef.current = true

    void completeCartOrder
      .mutateAsync()
      .then((order) => {
        navigate({
          to: "/$countryCode/order/$orderId/confirmed",
          params: { countryCode, orderId: order.id },
          // Carry the confirmation token (the completed cart id) so the receipt
          // survives a reload / cross-device open via the guest-safe endpoint.
          search: { cart: cart_id },
          replace: true,
        })
      })
      .catch((err) => {
        setError(
          err instanceof Error ? err.message : "Order completion failed.",
        )
        didCompleteRef.current = false
      })
  }, [cart_id, redirect_status, completeCartOrder, countryCode, navigate])

  if (error) {
    return (
      <div className="content-container py-16 flex flex-col items-center text-center gap-4">
        <h1 className="text-2xl font-semibold text-zinc-900">
          Payment Needs Attention
        </h1>
        <p className="max-w-md text-sm text-zinc-600">{error}</p>
        <Link
          to="/$countryCode/checkout"
          params={{ countryCode }}
          search={{ step: "payment" }}
        >
          <Button variant="primary" size="fit">
            Return to checkout
          </Button>
        </Link>
      </div>
    )
  }

  return (
    <div className="content-container py-16 flex flex-col items-center text-center gap-4">
      <Loading />
      <h1 className="text-2xl font-semibold text-zinc-900">
        Completing your order...
      </h1>
      <p className="max-w-md text-sm text-zinc-600">
        Please keep this page open while we confirm your +Bake pickup order.
      </p>
    </div>
  )
}
